import { MoreLinks } from "../components/MoreLinks.tsx";
import { RetiredPlayers } from "../components/RetiredPlayers.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import type { View } from "../../common/types.ts";
import { useLocal } from "../util/local.ts";
import { helpers } from "../util/helpers.ts";
import React from "react";
import { showStatsByType } from "../../common/awards.ts";
import { getCol } from "../../common/getCol.ts";
import { RatingsStatsPopover } from "../components/RatingsStatsPopover/index.tsx";

type ActualProps = Exclude<
	View<"history">,
	{ invalidSeason: true; season: number }
>;

const NO_WINNER = "No winner";

const Winner = ({
	award,
	indentStats,
	p,
	season,
	userTid,
}: {
	award:
		| ActualProps["awards"]["individualAwards"][number]
		| ActualProps["awards"]["teamAwards"][number];
	indentStats?: boolean;
	p:
		| ActualProps["awards"]["individualAwards"][number]["winner"]
		| { pid?: undefined; pos?: string };
	season: number;
	userTid: number;
}) => {
	const stats = showStatsByType[award.showStats];
	if (!stats) {
		throw new Error("Invalid showStats");
	}

	if (!p) {
		return null;
	}

	if (p.pid === undefined) {
		return (
			<div>
				{p.pos} {NO_WINNER}
				<br />
				<br />
			</div>
		);
	}

	return (
		<>
			<div>
				<span className={p.stats.tid === userTid ? "table-info" : undefined}>
					{p.pos}{" "}
					<b>
						<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>
					</b>{" "}
					<RatingsStatsPopover pid={p.pid} season={season} />
					(
					<a
						href={helpers.leagueUrl([
							"roster",
							`${p.stats.abbrev}_${p.stats.tid}`,
							season,
						])}
					>
						{p.stats.abbrev}
					</a>
					)
				</span>
			</div>
			<div className={indentStats ? "ms-3" : undefined}>
				{stats
					.map(
						(stat) =>
							`${helpers.roundStat(p.statOverrides?.[stat] ?? p.stats[stat], stat)}${stat === "keyStats" ? "" : ` ${getCol(`stat:${stat}`).title}`}`,
					)
					.join(", ")}
			</div>
		</>
	);
};

const Teams = ({
	award,
	season,
	userTid,
}: {
	award: ActualProps["awards"]["teamAwards"][number];
	season: number;
	userTid: number;
}) => {
	const multipleTeams = award.winner.length > 1;

	return (
		<>
			{multipleTeams ? <h2>{award.name}</h2> : null}
			{award.winner.map((t, i) => {
				if (t.length === 0) {
					return null;
				}

				return (
					<div key={i} className="mb-3">
						{multipleTeams ? (
							<h3>{helpers.ordinal(i + 1)} team</h3>
						) : (
							<h2>{award.name} team</h2>
						)}
						{t.map((p, i) => {
							return (
								<div key={i}>
									{p ? (
										<Winner
											award={award}
											indentStats
											p={p}
											season={season}
											userTid={userTid}
										/>
									) : null}
								</div>
							);
						})}
					</div>
				);
			})}
		</>
	);
};

const splitTeamAwards = <T extends { numTeams: number }>(teamAwards: T[]) => {
	// Keep all conference/division variants of one award together. Splitting by
	// raw row count mixed All-Rookie with All-Defensive on narrow screens.
	const groups = Object.values(Object.groupBy(teamAwards, (row) => (row as any).name)).filter(
		(group): group is T[] => group !== undefined,
	);
	const teamAwards1: T[] = [];
	const teamAwards2: T[] = [];
	let size1 = 0;
	let size2 = 0;
	for (const group of groups) {
		const size = group.reduce((sum, row) => sum + row.numTeams, 0);
		const target = size1 <= size2;
		(target ? teamAwards1 : teamAwards2).push(...group);
		if (target) size1 += size;
		else size2 += size;
	}

	return { teamAwards1, teamAwards2 };
};

// Same award in different groups (e.g. AL MVP and NL MVP) becomes one category, in the configured order
const groupIndividualAwards = <T extends { baseName: string }>(awards: T[]) => {
	const categories: { name: string; awards: T[] }[] = [];
	for (const award of awards) {
		const category = categories.find((c) => c.name === award.baseName);
		if (category) {
			category.awards.push(award);
		} else {
			categories.push({ name: award.baseName, awards: [award] });
		}
	}
	return categories;
};

// Champion and second by record (best regular season records; there are no minor league playoffs). Only for a
// complete season; summaries from before the head-to-head games say so instead of naming anyone.
const MINORS_PARTIAL: Record<string, string> = {
	startedMidSeason:
		"the records started with the season already under way, so they don't cover the whole season",
	missingGames: "some organizations have no recorded games",
	incomplete: "not every game of the season was recorded",
};
const MinorsCompetition = ({
	competition,
}: {
	competition: NonNullable<ActualProps["minorLeague"]>["competition"];
}) => {
	if (!competition) {
		return (
			<p className="text-body-secondary">
				This season was played before minor league teams faced each other, so
				there are no standings and no champion.
			</p>
		);
	}
	const row = (tid: number | undefined) =>
		competition.standings.find((r) => r.tid === tid);
	const team = (r: (typeof competition.standings)[number], title: string) => (
		<div className="col-sm-6 mb-3">
			<h2>{title}</h2>
			<b>
				<a href={helpers.leagueUrl(["minor_league", `${r.abbrev}_${r.tid}`])}>
					{r.region} {r.name}
				</a>
			</b>
			<div>
				{r.w}-{r.l}, {r.rs} RS, {r.ra} RA
			</div>
		</div>
	);
	const champion = row(competition.champion);
	const runnerUp = row(competition.runnerUp);
	return competition.complete && champion ? (
		<div className="row">
			{team(champion, "Champion by Record")}
			{runnerUp ? team(runnerUp, "Second by Record") : null}
		</div>
	) : (
		<p className="text-body-secondary">
			No champion this season: {MINORS_PARTIAL[competition.reason ?? ""] ??
				"the records are incomplete"}
			. The partial standings are kept, but no title is given.
		</p>
	);
};

// Minors tab: end-of-season summary of the automatic minor league season, only from minor league stats
const MinorsSeasonSummary = ({
	summary,
	season,
}: {
	summary: ActualProps["minorLeague"];
	season: number;
}) => {
	if (!summary) {
		return (
			<p>
				No minor league summary for {season}. Summaries exist only for seasons
				played after the automatic minor league season was added; older
				seasons don't have enough data, so nothing is made up for them.
			</p>
		);
	}
	type P = NonNullable<typeof summary.mvp>;
	const fmt = (x: number, digits: number) => x.toFixed(digits).replace(/^0\./, ".");
	const statsText = (p: P) =>
		p.stats.ip !== undefined
			? `${p.stats.g} G (${p.stats.gs} GS), ${p.stats.ip!.toFixed(1)} IP, ${p.stats.so} SO, ${p.stats.era!.toFixed(2)} ERA, ${p.stats.whip!.toFixed(2)} WHIP`
			: `${p.stats.g} G, ${p.stats.pa} PA, ${fmt(p.stats.avg!, 3)} AVG, ${p.stats.hr} HR, ${p.stats.rbi} RBI, ${fmt(p.stats.ops!, 3)} OPS`;
	const who = (p: P) => (
		<>
			{p.pos}{" "}
			<b>
				<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>
			</b>{" "}
			<RatingsStatsPopover
				key={`${p.pid}:${season}`}
				pid={p.pid}
				season={season}
				minorLeague
				minorSummary={{ tid: p.tid, abbrev: p.abbrev, age: p.age, keyStats: statsText(p) }}
			/>{" "}
			({p.age},{" "}
			<a href={helpers.leagueUrl(["minor_league", `${p.abbrev}_${p.tid}`])}>
				{p.abbrev}
			</a>
			)
		</>
	);
	const award = (title: string, p: P | undefined) => (
		<div className="col-md-4 mb-3">
			<h2>{title}</h2>
			{p ? (
				<>
					<div>{who(p)}</div>
					<div>{statsText(p)}</div>
				</>
			) : (
				NO_WINNER
			)}
		</div>
	);
	return (
		<>
			<MinorsCompetition competition={summary.competition} />
			<p className="text-body-secondary">
				League: {fmt(summary.league.avg!, 3)} AVG,{" "}
				{fmt(summary.league.ops!, 3)} OPS, {summary.league.era!.toFixed(2)} ERA,{" "}
				{summary.numPlayers} players.
			</p>
			<div className="row">
				{award("Minor League MVP", summary.mvp)}
				{award("Best Pitcher", summary.bestPitcher)}
				{award("Home Run Leader", summary.hrLeader)}
			</div>
			<h2>All-Minors Team</h2>
			<details className="mb-3">
				<summary>How the All-Minors team is selected</summary>
				<p className="mb-1">Hitter score = PA × (OPS − league OPS). This measures offensive production above the league average; it is not WAR and does not include fielding. The team takes the highest score at each listed field position, then the best remaining hitter as DH. A home run title does not automatically grant a place.</p>
				<p className="mb-1">All-Minors hitters need at least 30% of the league's highest PA; MVP candidates need 50%. Pitchers use IP/9 × (league ERA − ERA), with five SP and two RP. No player is selected twice.</p>
				{summary.hrLeader && !summary.team.some((r) => r.player.pid === summary.hrLeader!.pid) ? (
					<p className="mb-0">
						{summary.hrLeader.name}: score {summary.hrLeader.score.toFixed(2)}.
						{summary.team.filter((r) => r.role === summary.hrLeader!.pos || r.role === "DH").map(({role, player: p}) => (
							<span key={role}> Selected {role}: {p.name}, score {p.score.toFixed(2)}.</span>
						))}
					</p>
				) : null}
			</details>
			<div className="table-responsive">
				<table className="table table-sm table-striped">
					<thead>
						<tr>
							<th>Role</th>
							<th>Player</th>
							<th>Stats</th>
						</tr>
					</thead>
					<tbody>
						{summary.team.map(({ role, player: p }, i) => (
							<tr key={i}>
								<td>{role}</td>
								<td>{who(p)}</td>
								<td>{statsText(p)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
};

const History = (props: View<"history">) => {
	const { invalidSeason, season } = props;

	useTitleBar({
		title: "Season Summary",
		jumpTo: true,
		jumpToSeason: season,
		dropdownView: "history",
		dropdownFields: {
			seasonsHistory: season,
		},
	});
	const { userTid } = useLocal(["userTid"]);
	// Links from the Minor League history open straight on the Minors tab (#minors)
	const [tab, setTab] = React.useState<"mlb" | "minors">(() =>
		typeof window !== "undefined" && window.location.hash === "#minors"
			? "minors"
			: "mlb",
	);

	if (invalidSeason) {
		return (
			<>
				<h2>Error</h2>
				<p>Invalid season.</p>
			</>
		);
	}

	const { awards, champ, confs, retiredPlayers, retiredStat, recordEvents } = props;

	const { teamAwards1, teamAwards2 } = splitTeamAwards(awards.teamAwards);

	const groupedIndividualAwardsPlayoffs = Object.values(
		Object.groupBy(awards.individualAwardsPlayoffs, (award) => award.shortName),
	);

	return (
		<>
			<MoreLinks type="awards" page="history" season={season} />

			{props.isBaseball ? (
				<ul className="nav nav-tabs mb-3">
					{(["mlb", "minors"] as const).map((key) => (
						<li className="nav-item" key={key}>
							<button
								className={`nav-link${tab === key ? " active" : ""}`}
								onClick={() => setTab(key)}
							>
								{key === "mlb" ? "MLB" : "Minors"}
							</button>
						</li>
					))}
				</ul>
			) : null}

			{props.isBaseball && tab === "minors" ? (
				<MinorsSeasonSummary summary={props.minorLeague} season={season} />
			) : (
			<>
				<div className="row g-3 mb-3" style={props.isBaseball && recordEvents.length > 0 ? { minHeight: "20rem" } : undefined}>
					<div className={props.isBaseball && recordEvents.length > 0 ? "col-6 col-lg-4" : "col-12"}>
						<h2>League Champs</h2>
						{champ ? (
							<div>
								<div className="mb-3">
									<span
										className={
											champ.tid === userTid ? "table-info" : undefined
										}
									>
										<b>
											<a
												href={helpers.leagueUrl([
													"roster",
													`${champ.seasonAttrs.abbrev}_${champ.tid}`,
													season,
												])}
											>
												{champ.seasonAttrs.region} {champ.seasonAttrs.name}
											</a>
										</b>
									</span>
									<br />
									<a href={helpers.leagueUrl(["playoffs", season])}>
										Playoff bracket
									</a>
								</div>
								{groupedIndividualAwardsPlayoffs.map((groupedAwards, i) => {
									const firstAward = groupedAwards?.[0];
									if (!firstAward) {
										return;
									}
									return (
										<React.Fragment key={i}>
											<div className="mb-3">
												{helpers.plural(
													firstAward.name,
													groupedAwards.length,
												)}
												:{" "}
												{groupedAwards.map((award, i) => {
													return award.winner ? (
														<Winner
															key={i}
															award={award}
															p={award.winner}
															season={awards.season}
															userTid={userTid}
														/>
													) : (
														NO_WINNER
													);
												})}
											</div>
										</React.Fragment>
									);
								})}

							</div>
						) : (
							<div className="mb-3">{NO_WINNER}</div>
						)}
						<h2>Best Record</h2>
						{Array.from(awards.bestRecordConfs.entries()).map(([cid, t]) =>
							t ? (
								<div key={cid} className="mb-3">
									{confs[cid]?.name}:<br />
									<span
										className={t.tid === userTid ? "table-info" : undefined}
									>
										<a
											href={helpers.leagueUrl([
												"roster",
												`${t.seasonAttrs.abbrev}_${t.tid}`,
												season,
											])}
										>
											{t.seasonAttrs.region} {t.seasonAttrs.name}
										</a>{" "}
										({helpers.formatRecord(t.seasonAttrs)})
									</span>
									<br />
								</div>
							) : null,
						)}
					</div>
					{props.isBaseball && recordEvents.length > 0 ? (
						<aside className="col-6 col-lg-8 position-relative">
							<div className="position-absolute top-0 bottom-0 start-0 end-0 overflow-auto px-2" style={{ overflowWrap: "anywhere" }} tabIndex={0} aria-label="Records broken">
								<h2>Records broken</h2>
								{recordEvents.map((event) => (
									<div className="mb-3" key={event.eid} dangerouslySetInnerHTML={{ __html: event.text }} />
								))}
							</div>
						</aside>
					) : null}
				</div>
				<div className="row g-3">
					<div className="col-xl-5 col-lg-6 col-12">
						{/* One row per award, its groups (conferences/divisions from Award Settings) side by side */}
						{groupIndividualAwards(awards.individualAwards).map((category) => {
							return (
								<React.Fragment key={category.name}>
									<h2>{category.name}</h2>
									<div className="row g-2 mb-3">
										{category.awards.map((award, i) => (
											<div
												key={i}
												className={
													category.awards.length === 1
														? "col-12"
														: category.awards.length === 2
															? "col-6"
															: "col-6 col-lg-4"
												}
											>
												{award.groupPrefix ? (
													<div className="fw-bold">{award.groupPrefix}</div>
												) : null}
												{award.winner ? (
													<Winner
														award={award}
														p={award.winner}
														season={awards.season}
														userTid={userTid}
													/>
												) : (
													NO_WINNER
												)}
											</div>
										))}
									</div>
								</React.Fragment>
							);
						})}
					</div>
					<div className="col-xl-2 col-lg-3 col-6">
						{teamAwards1.map((award, i) => {
							return (
								<Teams
									key={i}
									award={award}
									season={awards.season}
									userTid={userTid}
								/>
							);
						})}
					</div>
					<div className="col-xl-2 col-lg-3 col-6">
						{teamAwards2.map((award, i) => {
							return (
								<Teams
									key={i}
									award={award}
									season={awards.season}
									userTid={userTid}
								/>
							);
						})}
					</div>
					<div className="col-xl-3 col-12">
						<RetiredPlayers
							retiredPlayers={retiredPlayers}
							retiredStat={retiredStat}
							season={season}
							userTid={userTid}
						/>
					</div>
				</div>
			</>
			)}
		</>
	);
};

export default History;
